import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Activity,
    AlertTriangle,
    BellOff,
    Bot,
    Calculator,
    CheckCircle2,
    Clock,
    FileText,
    Layers,
    Loader2,
    Power,
    RefreshCw,
    Save,
    Scale,
    Send,
    Shield,
    Sparkles,
    ToggleLeft,
    ToggleRight,
    Users,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { whatsappMerkez } from "../../lib/api";

const IKON_MAP = {
    power: Power,
    clock: Clock,
    send: Send,
    file: FileText,
    bot: Bot,
    sparkles: Sparkles,
    check: CheckCircle2,
    scale: Scale,
    alert: AlertTriangle,
    chart: Activity,
    calculator: Calculator,
    user: Users,
    shield: Shield,
    users: Users,
    refresh: RefreshCw,
};

const GRUP_SIRASI = ["temel", "evrak", "otonom", "tahkim", "lifecycle", "kayit", "inbox", "araci"];

const GRUP_RENK = {
    temel: "border-slate-500/30 from-slate-500/10",
    evrak: "border-emerald-500/35 from-emerald-500/10",
    otonom: "border-violet-500/35 from-violet-500/10",
    tahkim: "border-amber-500/35 from-amber-500/10",
    lifecycle: "border-sky-500/35 from-sky-500/10",
    kayit: "border-rose-500/35 from-rose-500/10",
    inbox: "border-cyan-500/35 from-cyan-500/10",
    araci: "border-fuchsia-500/35 from-fuchsia-500/10",
};

function ModulToggle({ aktif, onToggle, disabled }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={disabled}
            className={aktif ? "text-emerald-400" : "text-slate-500"}
        >
            {aktif ? <ToggleRight className="h-9 w-9" /> : <ToggleLeft className="h-9 w-9" />}
        </button>
    );
}

export default function WhatsAppModulMerkezi({ runBusy, busyKey }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({});
    const [grupFiltre, setGrupFiltre] = useState("");
    const [seciliModul, setSeciliModul] = useState("otonom_eksik_evrak");

    const yukle = useCallback(async () => {
        setLoading(true);
        try {
            const res = await whatsappMerkez.modulMerkezi(7);
            setData(res);
            const d = {};
            (res.moduller || []).forEach((m) => {
                d[m.id] = { aktif: m.aktif, alt: { ...(m.alt || {}) } };
            });
            setDraft(d);
        } catch (e) {
            toast.error("Modül merkezi yüklenemedi.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        yukle();
    }, [yukle]);

    const moduller = useMemo(() => data?.moduller || [], [data]);
    const grupEtiket = data?.grup_etiket || {};

    const gruplu = useMemo(() => {
        const g = {};
        moduller.forEach((m) => {
            const grup = m.grup || "temel";
            if (grupFiltre && grup !== grupFiltre) return;
            if (!g[grup]) g[grup] = [];
            g[grup].push(m);
        });
        return g;
    }, [moduller, grupFiltre]);

    const secili = useMemo(
        () => moduller.find((m) => m.id === seciliModul) || moduller[0],
        [moduller, seciliModul],
    );

    const patchModul = (id, patch) => {
        setDraft((prev) => ({
            ...prev,
            [id]: {
                aktif: patch.aktif ?? prev[id]?.aktif,
                alt: { ...(prev[id]?.alt || {}), ...(patch.alt || {}) },
            },
        }));
    };

    const kaydet = async () => {
        setSaving(true);
        try {
            const items = Object.entries(draft).map(([id, val]) => ({
                id,
                aktif: val.aktif,
                alt: val.alt,
            }));
            const res = await whatsappMerkez.modulMerkeziKaydet({ items });
            setData(res);
            toast.success("Modül ayarları kaydedildi.");
        } catch (e) {
            toast.error("Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    const topluKapat = async (grup) => {
        await runBusy?.(`modul-kapat-${grup}`, async () => {
            const res = await whatsappMerkez.modulTopluKapat({ grup });
            setData(res);
            const d = {};
            (res.moduller || []).forEach((m) => {
                d[m.id] = { aktif: m.aktif, alt: { ...(m.alt || {}) } };
            });
            setDraft(d);
            toast.success("Seçili modüller kapatıldı.");
        });
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" /> Modül merkezi yükleniyor…
            </div>
        );
    }

    const ozet = data?.ozet || {};

    return (
        <section className="space-y-5">
            <div className="rounded-2xl border border-[#25d366]/25 bg-gradient-to-br from-[#0b141a] via-[#0b1220] to-[#121A2F] p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <span className="h-12 w-12 rounded-xl bg-[#25d366]/15 border border-[#25d366]/35 flex items-center justify-center shrink-0">
                            <Layers className="h-6 w-6 text-[#25d366]" />
                        </span>
                        <div>
                            <h2 className="font-display text-xl text-white">WhatsApp Modül Merkezi</h2>
                            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
                                Eksik evrak, otonom hatırlatma, tahkim zinciri ve tüm WhatsApp bildirimlerini
                                modül modül açıp kapatın. Son 7 gün gönderim takibi burada.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={yukle} disabled={loading}>
                            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-500/40 text-rose-300"
                            onClick={() => topluKapat("all_evrak")}
                            disabled={busyKey?.startsWith("modul-kapat")}
                        >
                            <BellOff className="h-4 w-4 mr-1" /> Tüm evrak spam&apos;i kapat
                        </Button>
                        <Button size="sm" className="bg-[#25d366] text-white" onClick={kaydet} disabled={saving}>
                            <Save className="h-4 w-4 mr-1" /> {saving ? "Kaydediliyor…" : "Kaydet"}
                        </Button>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Aktif modül</div>
                        <div className="text-2xl font-semibold text-[#25d366] mt-1">
                            {data?.aktif_sayisi}/{data?.toplam_modul}
                        </div>
                    </div>
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">Evrak WA (7g)</div>
                        <div className="text-2xl font-semibold text-emerald-300 mt-1">{ozet.evrak_7g ?? 0}</div>
                    </div>
                    <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-violet-300/80">Otonom evrak</div>
                        <div className="text-lg font-semibold mt-1">
                            {ozet.otonom_eksik_aktif ? (
                                <span className="text-emerald-400">Açık</span>
                            ) : (
                                <span className="text-rose-400">Kapalı ✓</span>
                            )}
                        </div>
                    </div>
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Bekleyen kuyruk</div>
                        <div className="text-2xl font-semibold text-amber-200 mt-1">{ozet.bekleyen_kuyruk ?? 0}</div>
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => setGrupFiltre("")}
                    className={`px-3 py-1.5 rounded-lg text-xs border ${!grupFiltre ? "border-[#25d366]/50 text-[#25d366] bg-[#25d366]/10" : "border-white/10 text-slate-400"}`}
                >
                    Tümü
                </button>
                {GRUP_SIRASI.map((g) =>
                    grupEtiket[g] ? (
                        <button
                            key={g}
                            type="button"
                            onClick={() => setGrupFiltre(g === grupFiltre ? "" : g)}
                            className={`px-3 py-1.5 rounded-lg text-xs border ${grupFiltre === g ? "border-[#25d366]/50 text-[#25d366] bg-[#25d366]/10" : "border-white/10 text-slate-400"}`}
                        >
                            {grupEtiket[g]}
                        </button>
                    ) : null,
                )}
            </div>

            <div className="grid xl:grid-cols-[1.15fr_0.85fr] gap-5">
                <div className="space-y-4">
                    {GRUP_SIRASI.map((grup) => {
                        const liste = gruplu[grup];
                        if (!liste?.length) return null;
                        const renk = GRUP_RENK[grup] || GRUP_RENK.temel;
                        return (
                            <div
                                key={grup}
                                className={`rounded-xl border bg-gradient-to-br to-transparent p-4 space-y-3 ${renk}`}
                            >
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                                    {grupEtiket[grup] || grup}
                                </h3>
                                <div className="space-y-2">
                                    {liste.map((m) => {
                                        const Icon = IKON_MAP[m.ikon] || Zap;
                                        const d = draft[m.id] || { aktif: m.aktif, alt: m.alt || {} };
                                        const st = m.istatistik || {};
                                        return (
                                            <div
                                                key={m.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setSeciliModul(m.id)}
                                                onKeyDown={(e) => e.key === "Enter" && setSeciliModul(m.id)}
                                                className={`rounded-lg border p-3 cursor-pointer transition-all ${
                                                    seciliModul === m.id
                                                        ? "border-[#25d366]/50 bg-[#25d366]/5"
                                                        : "border-white/10 bg-[#0B1220]/60 hover:border-white/20"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex items-start gap-2 min-w-0">
                                                        <Icon className="h-4 w-4 text-[#25d366] shrink-0 mt-0.5" />
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-medium text-white">{m.label}</span>
                                                                {m.kritik ? (
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                                                        SPAM riski
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{m.aciklama}</p>
                                                            <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
                                                                <span className="text-slate-500">{m.tetik}</span>
                                                                {st.gonderildi_7g > 0 ? (
                                                                    <span className="text-emerald-400">{st.gonderildi_7g} gönderim (7g)</span>
                                                                ) : null}
                                                                {st.bekleyen > 0 ? (
                                                                    <span className="text-amber-400">{st.bekleyen} kuyrukta</span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ModulToggle
                                                        aktif={d.aktif !== false}
                                                        onToggle={(e) => {
                                                            e.stopPropagation();
                                                            patchModul(m.id, { aktif: !(d.aktif !== false) });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="space-y-4">
                    {secili ? (
                        <div className="rounded-xl border border-[#25d366]/25 bg-[#0B1220]/80 p-4 space-y-4 sticky top-4">
                            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#25d366]" />
                                {secili.label}
                            </h3>
                            <p className="text-xs text-slate-400">{secili.aciklama}</p>
                            <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="rounded-lg bg-black/30 p-2">
                                    <div className="text-lg font-semibold text-emerald-400">
                                        {secili.istatistik?.gonderildi_7g ?? 0}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Gönderildi (7g)</div>
                                </div>
                                <div className="rounded-lg bg-black/30 p-2">
                                    <div className="text-lg font-semibold text-amber-300">
                                        {secili.istatistik?.bekleyen ?? 0}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Kuyrukta</div>
                                </div>
                            </div>
                            {(secili.alt_ayarlar || []).length > 0 ? (
                                <div className="space-y-2 border-t border-white/10 pt-3">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Alt ayarlar</div>
                                    {(secili.alt_ayarlar || []).map((a) => (
                                        <label key={a.key} className="block space-y-1">
                                            <span className="text-[11px] text-slate-400">{a.label}</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={30}
                                                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"
                                                value={draft[secili.id]?.alt?.[a.key] ?? a.varsayilan ?? ""}
                                                onChange={(e) =>
                                                    patchModul(secili.id, {
                                                        alt: { [a.key]: parseInt(e.target.value, 10) || a.varsayilan },
                                                    })
                                                }
                                            />
                                        </label>
                                    ))}
                                </div>
                            ) : null}
                            {secili.sablon_kodlari?.length ? (
                                <div className="text-[11px] text-slate-500">
                                    Şablonlar:{" "}
                                    <span className="text-slate-300">{secili.sablon_kodlari.join(", ")}</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-white/10 bg-[#0B1220]/60 p-4">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                            <Activity className="h-4 w-4 text-sky-400" />
                            Son gönderimler
                        </h3>
                        <ul className="space-y-2 max-h-[320px] overflow-y-auto">
                            {(data?.son_gonderimler || []).map((g) => (
                                <li
                                    key={g.id}
                                    className="flex justify-between gap-2 text-[11px] border-b border-white/5 pb-2"
                                >
                                    <div className="min-w-0">
                                        <span className="text-[#25d366] font-mono">{g.sablon_kod || "manuel"}</span>
                                        <span className="text-slate-500 ml-2">{g.muvekkil}</span>
                                        {g.sigorta_dosyasi__dosya_no ? (
                                            <span className="text-slate-600 ml-1">· {g.sigorta_dosyasi__dosya_no}</span>
                                        ) : null}
                                    </div>
                                    <span
                                        className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] ${
                                            g.durum === "GONDERILDI"
                                                ? "bg-emerald-500/20 text-emerald-300"
                                                : g.durum === "HATA"
                                                  ? "bg-rose-500/20 text-rose-300"
                                                  : "bg-amber-500/20 text-amber-300"
                                        }`}
                                    >
                                        {g.durum}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
